import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AppHeader from './AppHeader';
import NewsDisplay from './NewsDisplay';
import NextRehearsal from './NextRehearsal';
import Gallery from './Gallery';
import SocialMediaCTAs from './SocialMediaCTAs';
import NewsManager from './NewsManager';
import MusicXMLPlayer from './MusicXMLPlayer';
import EarTraining from './EarTraining';
import VocalExercises from './VocalExercises';
import AboutUs from './AboutUs';
import Calendar from './Calendar';
import PreviousRehearsalNotes from './PreviousRehearsalNotes';
import PracticeCTA from './PracticeCTA';
import { logPageView } from '../utils/activityLogger';
import { burstMusicNotes } from '../utils/noteConfetti';
import '../styles/app-layout.css';

const AppLayout = () => {
  const { user, loading } = useAuth();

  // State that needs to be shared between header and player
  const [fileName, setFileName] = useState(null);
  const [tipRefreshTrigger, setTipRefreshTrigger] = useState(0);
  const [newsRefreshTrigger, setNewsRefreshTrigger] = useState(0);
  const [rehearsalRefreshTrigger, setRehearsalRefreshTrigger] = useState(0);
  const [isInTipPlacementMode, setIsInTipPlacementMode] = useState(false);
  const osmdRef = useRef(null);
  const [visibleParts, setVisibleParts] = useState([]);
  const [partControls, setPartControls] = useState([]);

  // News management state
  const [showNewsManager, setShowNewsManager] = useState(false);
  const [newsManagerMode, setNewsManagerMode] = useState('list'); // 'list', 'create', 'edit'
  const [editingNewsItem, setEditingNewsItem] = useState(null);

  // Registration dialog trigger (set by AuthButton)
  const [triggerRegisterDialog, setTriggerRegisterDialog] = useState(null);

  // Returning-user welcome-back banner.
  // Captured at first render (before any fresh sign-in this session writes to
  // localStorage), so a persisted "musiker-user" means this is a returning visit
  // rather than a brand-new sign-in.
  const [wasReturningVisitor] = useState(() => {
    try {
      return localStorage.getItem('musiker-user') !== null;
    } catch {
      return false;
    }
  });
  const showWelcomeBack = !loading && !!user && wasReturningVisitor;
  const welcomeFirstName = showWelcomeBack
    ? ((user.displayName || user.name || '').trim().split(/\s+/)[0] || '')
    : '';

  // Shortly after the banner appears, celebrate with a burst of musical-note
  // confetti from the pill (skipped for prefers-reduced-motion).
  const welcomeBannerRef = useRef(null);
  useEffect(() => {
    if (!showWelcomeBack) return;
    let cleanupBurst = null;
    const timer = setTimeout(() => {
      cleanupBurst = burstMusicNotes(welcomeBannerRef.current);
    }, 1400);
    return () => {
      clearTimeout(timer);
      if (cleanupBurst) cleanupBurst();
    };
  }, [showWelcomeBack]);

  // The fixed header's height varies with viewport width and localized title
  // wrapping, and can exceed the content area's static margin-top — hiding
  // the top of the content behind the header. Publish the real header height
  // as a CSS variable so the content area starts exactly below it.
  useEffect(() => {
    const headerEl = document.querySelector('.app-header');
    if (!headerEl || typeof ResizeObserver === 'undefined') return;
    const update = () => {
      document.documentElement.style.setProperty(
        '--app-header-height',
        `${Math.ceil(headerEl.getBoundingClientRect().height)}px`
      );
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(headerEl);
    window.addEventListener('resize', update);
    // Custom fonts re-wrap the title and change the header height
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(update).catch(() => {});
    }
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  // Scroll to top on mount to prevent browser scroll restoration issues
  useEffect(() => {
    // Aggressive scroll to top with multiple attempts
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    // Scroll again after short delays to override browser scroll restoration
    const timers = [
      setTimeout(() => {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }, 50),
      setTimeout(() => {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }, 150),
      setTimeout(() => {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }, 300)
    ];

    return () => timers.forEach(timer => clearTimeout(timer));
  }, []);

  // Log page view only for meaningful visits (after auth has loaded)
  useEffect(() => {
    if (!loading) {
      // Check if we've already logged this session
      const hasLoggedThisSession = sessionStorage.getItem('page-view-logged') === 'true';
      
      if (!hasLoggedThisSession) {
        // Only log if:
        // 1. User is signed in (returning user visit), OR
        // 2. User is not signed in AND has never signed in before (true first-time visitor)
        const hasSignedInBefore = localStorage.getItem('musiker-has-signed-in') === 'true';
        const shouldLogPageView = user || !hasSignedInBefore;
        
        if (shouldLogPageView) {
          logPageView(user, 'Home Page', {
            hasUser: !!user,
            isFirstTimeVisitor: !hasSignedInBefore,
            timestamp: Date.now()
          });
          
          // Mark that we've logged this session
          sessionStorage.setItem('page-view-logged', 'true');
        }
      }
    }
  }, [loading, user]); // Wait for auth to finish loading

  const handleLoadFileRequest = (fileContent, name) => {
    setFileName(name);
    // This will be handled by the MusicXMLPlayer
  };

  // News management handlers
  const handleAddNews = () => {
    setEditingNewsItem(null);
    setNewsManagerMode('create');
    setShowNewsManager(true);
  };

  const handleEditNews = (newsItem) => {
    setEditingNewsItem(newsItem);
    setNewsManagerMode('edit');
    setShowNewsManager(true);
  };

  const handleDeleteNews = async (newsId) => {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      
      const response = await fetch(`${API_BASE}/api/content?operation=deleteContent&contentId=${newsId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userRole: user.role,
          userId: user.id
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete news item');
      }

      // Refresh news display
      setNewsRefreshTrigger(prev => prev + 1);
    } catch (err) {
      console.error('Delete news error:', err);
      alert(`Failed to delete news item: ${err.message}`);
    }
  };

  const handleNewsManagerClose = () => {
    setShowNewsManager(false);
    setNewsManagerMode('list');
    setEditingNewsItem(null);
    // Refresh news display when manager closes
    setNewsRefreshTrigger(prev => prev + 1);
  };

  // Calculate visible parts from partControls
  const calculateVisibleParts = (controls) => {
    return controls.map(p => p.InstrumentId).filter(id => 
      controls.find(pc => pc.InstrumentId === id)?.visible !== false
    );
  };

  // MusicXML Player component - reused in conditional rendering
  const musicXMLPlayerComponent = (
    <MusicXMLPlayer
      fileName={fileName}
      setFileName={setFileName}
      osmdRef={osmdRef}
      partControls={partControls}
      setPartControls={setPartControls}
      tipRefreshTrigger={tipRefreshTrigger}
      onTipsUpdated={() => setTipRefreshTrigger(prev => prev + 1)}
      isInTipPlacementMode={isInTipPlacementMode}
      setIsInTipPlacementMode={setIsInTipPlacementMode}
    />
  );

  return (
    <div className="app-layout">
      <AppHeader
        currentFileName={fileName}
        osmdInstance={osmdRef.current}
        visibleParts={calculateVisibleParts(partControls)}
        onTipsUpdated={() => setTipRefreshTrigger(prev => prev + 1)}
        onTipPlacementModeChange={setIsInTipPlacementMode}
        onLoadFileRequest={handleLoadFileRequest}
        onNewsUpdated={() => setNewsRefreshTrigger(prev => prev + 1)}
        onRegisterTrigger={setTriggerRegisterDialog}
      />
      <main className="app-main-content">
        <div className="app-content-container">
          {/* Returning-user welcome-back banner */}
          {showWelcomeBack && (
            <div className="welcome-back-banner" role="status" aria-live="polite" ref={welcomeBannerRef}>
              <span className="welcome-back-wave" aria-hidden="true">👋</span>
              <span className="welcome-back-text">
                Welcome back
                {welcomeFirstName && <>, <strong>{welcomeFirstName}</strong></>}!
              </span>
            </div>
          )}

          {/* Practice CTA - Show at top for non-logged-in users */}
          {!user && (
            <PracticeCTA
              onSignIn={() => {
                if (triggerRegisterDialog) {
                  triggerRegisterDialog();
                }
              }}
            />
          )}

          {/* MusicXML Player Section - Show at top when user is signed in */}
          {user && musicXMLPlayerComponent}

          {/* Next Rehearsal Section - Show for non-visitor signed-in users before Previous Rehearsal Notes */}
          {user && user.role !== 'visitor' && (
            <NextRehearsal refreshTrigger={rehearsalRefreshTrigger} />
          )}

          {/* Previous Rehearsal Notes Section - Show for non-visitor signed-in users */}
          {user && user.role !== 'visitor' && <PreviousRehearsalNotes />}

          {/* About Us Section */}
          <AboutUs />

          {/* Next Rehearsal Section - Show for visitors and non-signed-in users */}
          {(!user || user.role === 'visitor') && (
            <NextRehearsal refreshTrigger={rehearsalRefreshTrigger} />
          )}

          {/* News Section */}
          <NewsDisplay
            maxItems={3}
            refreshTrigger={newsRefreshTrigger}
            onAddNews={handleAddNews}
            onEditNews={handleEditNews}
            onDeleteNews={handleDeleteNews}
            defaultExpanded={true}
          />

          {/* Calendar Section */}
          <Calendar />

          {/* Gallery Section */}
          <Gallery
            user={user}
            isOpen={true}
          />

          {/* Ear Training Section */}
          <EarTraining />

          {/* Vocal Exercises Section */}
          <VocalExercises />

          {/* Social Media CTAs - Only show on mobile */}
          <SocialMediaCTAs variant="mobile" />

          {/* MusicXML Player Section - Show at bottom when user is NOT signed in */}
          {!user && musicXMLPlayerComponent}
        </div>
      </main>

      {/* News Manager Modal */}
      {showNewsManager && (
        <NewsManager
          isOpen={showNewsManager}
          onClose={handleNewsManagerClose}
          initialMode={newsManagerMode}
          initialEditItem={editingNewsItem}
        />
      )}
    </div>
  );
};

export default AppLayout;